import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { AppShell } from '../components/AppShell/AppShell';
import { RouteGuard } from './route-guard/RouteGuard';
import { RoleHomeDispatcher } from './RoleHomeDispatcher';
import { UnknownAddress } from './route-guard/UnknownAddress';
import { PublicShell } from '../components/AppShell/PublicShell';
import { PlaceholderPage } from '../components/PlaceholderPage';
import { HomePage } from '../pages/HomePage';
import { DevLoginPage } from '../pages/DevLoginPage';
import { RoleSelectPage } from '../pages/RoleSelectPage';
import { AuthNoticePage } from '../pages/AuthNoticePage';
import { LoginSuccessPage } from '../pages/LoginSuccessPage';
import { StockReportingPage } from '../pages/operator/reporting/StockReportingPage';
import { StockImportPage } from '../pages/operator/reporting/import/StockImportPage';
import { DraftsPage } from '../pages/operator/drafts/DraftsPage';
import { SubmissionRegisterPage } from '../pages/operator/reports/SubmissionRegisterPage';
import { SubmissionDetailPage } from '../pages/operator/reports/SubmissionDetailPage';
import { OperatorCorrectionsPage } from '../pages/operator/corrections/OperatorCorrectionsPage';
import { CorrectionQueuePage } from '../pages/system-admin/CorrectionQueuePage';
import { CorrectionRequestDetailPage } from '../pages/system-admin/CorrectionRequestDetailPage';
import { AggregateDetailPage } from '../pages/system-admin/AggregateDetailPage';
import { SystemConfigPage } from '../pages/system-admin/SystemConfigPage';
import { MedicinesPage } from '../pages/system-admin/MedicinesPage';
import { MedicineFormPage } from '../pages/system-admin/MedicineFormPage';
import { MedicinesImportPage } from '../pages/system-admin/MedicinesImportPage';
import { MedicineCodeDetailPage } from '../pages/system-admin/MedicineCodeDetailPage';
import { RegistrarsPage } from '../pages/system-admin/RegistrarsPage';
import { RegistrarEditRedirect, RegistrarFormPage } from '../pages/system-admin/RegistrarFormPage';
import { RegistrarDetailPage } from '../pages/system-admin/RegistrarDetailPage';
import { AlertRulesPage } from '../pages/system-admin/AlertRulesPage';
import { AlertRuleFormPage } from '../pages/system-admin/AlertRuleFormPage';
import { AlertRuleDetailPage } from '../pages/system-admin/AlertRuleDetailPage';
import { AlertTriggersPage } from '../pages/system-admin/AlertTriggersPage';
import { NotificationTemplatesPage } from '../pages/system-admin/NotificationTemplatesPage';
import { NotificationTemplateFormPage } from '../pages/system-admin/NotificationTemplateFormPage';
import { NotificationPoliciesPage } from '../pages/system-admin/NotificationPoliciesPage';
import { NotificationPolicyFormPage } from '../pages/system-admin/NotificationPolicyFormPage';
import { NotificationChannelsPage } from '../pages/system-admin/NotificationChannelsPage';
import { NotificationChannelFormPage } from '../pages/system-admin/NotificationChannelFormPage';
import { WatchedGroupsPage } from '../pages/system-admin/WatchedGroupsPage';
import { WatchedGroupFormPage } from '../pages/system-admin/WatchedGroupFormPage';
import { WatchedGroupHistoryPage } from '../pages/system-admin/WatchedGroupHistoryPage';
import { AggregatesPage } from '../pages/system-admin/AggregatesPage';
import { AggregateFormPage } from '../pages/system-admin/AggregateFormPage';
import { GeographyPage } from '../pages/system-admin/GeographyPage';
import { GeographyHistoryPage } from '../pages/system-admin/GeographyHistoryPage';
import { NomenclatorHistoryPage } from '../pages/system-admin/NomenclatorHistoryPage';
import { MconnectCredentialsPage } from '../pages/system-admin/MconnectCredentialsPage';
import { McnonnectEventsPage } from '../pages/system-admin/McnonnectEventsPage';
import { ObserverRolesPage } from '../pages/system-admin/ObserverRolesPage';
import { CredentialsPage } from '../pages/registrar-admin/CredentialsPage';
import { SitesPage } from '../pages/registrar-admin/SitesPage';
import { SiteFormPage } from '../pages/registrar-admin/SiteFormPage';
import { StockChartTable } from '../components/StockChartTable/StockChartTable';
import { CrossIMSPStockPage } from '../pages/stocks/CrossIMSPStockPage';
import { ExpiringLotsPage } from '../pages/dashboards/ExpiringLotsPage';
import { DashboardsPage } from '../pages/dashboards/DashboardsPage';
import { DashboardsAdminPage } from '../pages/system-admin/DashboardsAdminPage';
import { DashboardFormPage } from '../pages/system-admin/DashboardFormPage';
import { DEV_LOGIN_ENABLED } from './dev-login-flag';
import { BackClosesModal } from './back-closes-modal';

/** `/:role/reporting/drafts/:draftId`: one page instance per draft, so opening another starts clean. */
function DraftReportRoute(): JSX.Element {
  const { draftId = '' } = useParams<{ draftId: string }>();
  return <StockReportingPage key={`draft:${draftId}`} draftId={draftId} />;
}

function TranslatedPlaceholder({
  titleKey,
  descriptionKey,
}: {
  titleKey: string;
  descriptionKey: string;
}): JSX.Element {
  const { t } = useTranslate();
  return <PlaceholderPage title={t(titleKey)} description={t(descriptionKey)} />;
}

/**
 * The signed-in shell, with the history guard beside it: Back with a dialog open
 * closes the dialog (ACCS-11). Here rather than inside AppShell because only the
 * data router can host its blocker.
 * per docs/shape-ops-readable-urls-and-history.md §4
 */
function SignedInShell(): JSX.Element {
  return (
    <>
      <BackClosesModal />
      <AppShell />
    </>
  );
}

function NotFoundPage(): JSX.Element {
  return (
    <TranslatedPlaceholder
      titleKey="placeholder.not_found.title"
      descriptionKey="placeholder.not_found.description"
    />
  );
}

export { DEV_LOGIN_ENABLED };

export const router = createBrowserRouter([
  // Caiet ACCS-10 wants the support contact on every page, so the routes that
  // render outside `AppShell` share `PublicShell` for its footer (spec 002 FR-011).
  {
    element: <PublicShell />,
    children: [
      {
        path: '/',
        element: <HomePage />,
      },
      // `/auth/login` and `/auth/callback` were here: two placeholder screens
      // reading "va fi implementată în faza UC10", from before UC10 shipped. The
      // BFF's own `/api/v1/auth/login` and `POST /api/v1/auth/callback` are
      // different addresses and are untouched; the AssertionConsumerService
      // redirects to `/login-success` or `/auth/no-role` and has never sent
      // anyone to either of these. `tools/wireframe-parity/pairs.json` already
      // pairs the drawn login screen with `/` (spec 036 FR-11).
      //
      // caiet UC10-06 and its sibling state. Route segments stay English (R3);
      // the wireframe's own address for the first is /eroare-autorizare
      // (`wireframes/react/src/App.tsx:36`, read-only).
      {
        path: '/auth/no-role',
        element: <AuthNoticePage kind="no-role" />,
      },
      {
        path: '/auth/session-expired',
        element: <AuthNoticePage kind="session-expired" />,
      },
      // Where the MPass AssertionConsumerService has always sent readers after it
      // set the cookie (`apps/bff/src/auth/auth.controller.ts`) — with, until
      // now, nothing at this address but the not-found placeholder.
      {
        path: '/login-success',
        element: <LoginSuccessPage />,
      },
      {
        path: '/role-select',
        element: <RoleSelectPage />,
      },
      // Owner ruling of 2026-09-06: the development login stays, with its
      // predefined roles and users, until MPass is wired (spec 036 FR-05). It
      // stays where it is used — the Vite dev server, and the demo VM, whose
      // image build sets the flag from the one `DEV_AUTH_ENABLED` line in
      // deploy/env/bff.env — and is absent from any bundle built without the
      // flag, which is every Helm staging/production image: there the address
      // answers with the not-found page (caiet SEC-07, UC10-01; go-live tests
      // TC-UC10-01-03, TC-SEC-07-01). The address stays pinned here even when
      // off: left undeclared it would match `/:role` below and be treated as a
      // role called "dev-login" instead of answering not-found.
      // per docs/shape-ops-dev-login-gate.md §The gate
      {
        path: '/dev-login',
        element: DEV_LOGIN_ENABLED ? <DevLoginPage /> : <NotFoundPage />,
      },
    ],
  },
  {
    path: '/:role',
    element: <SignedInShell />,
    // Route-level authorisation: one pathless layout route between the shell and
    // every screen under it, so what a role may OPEN is decided in one place and
    // no page file learns about roles. AppShell keeps rendering, so a refusal is
    // the rail and the footer with a sentence in the workspace rather than a
    // blank page, and no leaf moves: matchRoutes still resolves every address to
    // the object it did before.
    // per docs/shape-ops-route-authorisation.md §Placement
    children: [
      {
        element: <RouteGuard />,
        children: [
          {
            index: true,
            element: <Navigate to="home" replace />,
          },
          {
            path: 'home',
            element: <RoleHomeDispatcher />,
          },
          {
            path: 'reporting',
            element: <Navigate to="new" replace />,
          },
          // Keyed, like the drafts route below: the two addresses render the same
          // component in the same place, and without a key React would carry one
          // report's state (its location, its dialog) into the other.
          {
            path: 'reporting/new',
            element: <StockReportingPage key="new" />,
          },
          // Spec 054 T064 (research R7, FR-024): the draft "Raporturi nefinalizate"
          // and the Acasă band open, loaded by its id and titled "Finalizează
          // raport". Under `reporting`, so the same authorisation as Raport nou:
          // both registrar roles and nobody else, while the BFF answers a draft
          // that is not the caller's with the same 404 as a missing one.
          {
            path: 'reporting/drafts/:draftId',
            element: <DraftReportRoute />,
          },
          // The wireframe's /import (owner decision D10). The screen shipped without
          // an address, so the band action on Acasa that leads here was withheld;
          // both land together. Like every sibling under this rail it inherits the
          // authorisation of its nav section — `reporting`, so both registrar roles
          // and nobody else — while the BFF still decides who may write, `POST
          // /stock/drafts` refusing a session with no Registrar scope.
          {
            path: 'reporting/import',
            element: <StockImportPage />,
          },
          // The wireframe's /nefinalizate. Route segments stay English (R3); the
          // label is Romanian and lives in the translation files.
          {
            path: 'drafts',
            element: <DraftsPage />,
          },
          {
            path: 'reports',
            element: <SubmissionRegisterPage />,
          },
          {
            path: 'reports/:id',
            element: <SubmissionDetailPage />,
          },
          {
            path: 'corrections',
            element: <OperatorCorrectionsPage />,
          },
          {
            path: 'approvals',
            element: <CorrectionQueuePage />,
          },
          {
            path: 'approvals/:id',
            element: <CorrectionRequestDetailPage />,
          },
          {
            path: 'stocks',
            element: <StockChartTable />,
          },
          {
            path: 'stocks/verify',
            element: <CrossIMSPStockPage />,
          },
          {
            path: 'expiring',
            element: <ExpiringLotsPage />,
          },
          // Owner's rulings of 2026-08-31 and 2026-09-01 #5: one reading entry, and it reads —
          // the dashboards this role may open, embedded kiosk (DashboardsPage). Authoring
          // and role sharing are `dashboards-admin`, below.
          //
          // Both addresses render the same screen since owner decision D9: the screen IS the
          // embed, with the chooser inside the frame, so /dashboards opens the first dashboard the
          // role reaches and /dashboards/:slug opens the named one. The deep links handed out
          // elsewhere keep resolving (FR-025).
          {
            path: 'dashboards',
            element: <DashboardsPage />,
          },
          {
            path: 'dashboards/:slug',
            element: <DashboardsPage />,
          },
          // The entry that used to lead to Grafana under its own name is gone from the menu; the
          // address is kept as a redirect so a bookmark still lands somewhere (FR-025).
          {
            path: 'grafana',
            element: <Navigate to="../dashboards" replace />,
          },
          {
            path: 'credentials',
            element: <CredentialsPage />,
          },
          {
            path: 'medicines',
            element: <MedicinesPage />,
          },
          {
            path: 'medicines/new',
            element: <MedicineFormPage />,
          },
          {
            // Before `medicines/:id`, whose param is a uuid: a code is not one, and a
            // literal segment must win over a dynamic sibling.
            path: 'medicines/code/:code',
            element: <MedicineCodeDetailPage />,
          },
          {
            path: 'medicines/import',
            element: <MedicinesImportPage />,
          },
          {
            path: 'medicines/history',
            element: <NomenclatorHistoryPage />,
          },
          {
            // The editor edits a CODE, so it is keyed on one — `medicines/:id/edit` was
            // the position editor, and a position no longer has a page.
            path: 'medicines/code/:code/edit',
            element: <MedicineFormPage />,
          },
          {
            // Reached from a code's record, so the trail can lead back to it. The
            // position-keyed route below stays: it is the same page, and the history it
            // lists is the position's either way.
            path: 'medicines/code/:code/history',
            element: <NomenclatorHistoryPage />,
          },
          {
            path: 'medicines/:id/history',
            element: <NomenclatorHistoryPage />,
          },
          {
            path: 'registrars',
            element: <RegistrarsPage />,
          },
          {
            path: 'registrars/new',
            element: <RegistrarFormPage />,
          },
          // The correction moved onto the file (spec 052); an old link lands there.
          {
            path: 'registrars/:id/edit',
            element: <RegistrarEditRedirect />,
          },
          {
            path: 'registrars/:idno',
            element: <RegistrarDetailPage />,
          },
          {
            path: 'alerts',
            element: <AlertRulesPage />,
          },
          {
            path: 'alerts/new',
            element: <AlertRuleFormPage />,
          },
          {
            path: 'alerts/:id',
            element: <AlertRuleDetailPage />,
          },
          {
            path: 'alerts/:id/edit',
            element: <AlertRuleFormPage />,
          },
          {
            // Sinteza folded into the Alerts list once that list gained the
            // evaluated state, next evaluation and last trigger
            // (docs/ui-shape-admin-aggregations-alerts.md §2.2 UC-ALRT-01).
            path: 'alerts/overview',
            element: <Navigate to="/system-admin/alerts" replace />,
          },
          // The wireframe's Declansari tab of Alerte si notificari
          // (wireframes/react/src/pages/AdminAlerte.tsx TABS).
          {
            path: 'alerts/triggers',
            element: <AlertTriggersPage />,
          },
          {
            path: 'alert-templates',
            element: <NotificationTemplatesPage />,
          },
          // The wireframe edits a template on a page of its own
          // (AdminAlerte.tsx:2834-2960, route `sabloane/:id/edit`). The audit found
          // this address answering 404 while the editor was a dialog.
          {
            path: 'alert-templates/new',
            element: <NotificationTemplateFormPage />,
          },
          {
            path: 'alert-templates/:id/edit',
            element: <NotificationTemplateFormPage />,
          },
          {
            path: 'alert-policies',
            element: <NotificationPoliciesPage />,
          },
          // The wireframe edits policies and channels on pages of their own
          // (AdminAlerte.tsx routes `politici/nou`, `politici/:id/edit`,
          // `canale/nou`, `canale/:id/edit`); they were dialogs until 2026-09-10.
          {
            path: 'alert-policies/new',
            element: <NotificationPolicyFormPage />,
          },
          {
            path: 'alert-policies/:id/edit',
            element: <NotificationPolicyFormPage />,
          },
          {
            path: 'alert-channels',
            element: <NotificationChannelsPage />,
          },
          {
            path: 'alert-channels/new',
            element: <NotificationChannelFormPage />,
          },
          {
            path: 'alert-channels/:id/edit',
            element: <NotificationChannelFormPage />,
          },
          // The watched medicine group A4, A5 and A10 refer to: a tab of Alerte si
          // notificari, System Administrator only (specs/060-agreed-alerts, T020-T022, T025).
          {
            path: 'watched-groups',
            element: <WatchedGroupsPage />,
          },
          {
            path: 'watched-groups/new',
            element: <WatchedGroupFormPage />,
          },
          {
            path: 'watched-groups/:id/edit',
            element: <WatchedGroupFormPage />,
          },
          {
            path: 'watched-groups/:id/history',
            element: <WatchedGroupHistoryPage />,
          },
          {
            path: 'aggregates',
            element: <AggregatesPage />,
          },
          {
            path: 'aggregates/new',
            element: <AggregateFormPage />,
          },
          {
            path: 'aggregates/:name',
            element: <AggregateDetailPage />,
          },
          {
            path: 'aggregates/:name/edit',
            element: <AggregateFormPage />,
          },
          {
            path: 'geography',
            element: <GeographyPage />,
          },
          {
            path: 'geography/history',
            element: <GeographyHistoryPage />,
          },
          // Three tools whose menu entries were removed because the wireframe puts
          // their function inside another screen. Each route outlives its entry
          // until that screen exists: deleting the route first would delete the
          // function, not move it. Retire each one in the batch that lands its
          // destination, together with the assertion in nav-model.spec.ts.
          //
          // Configurari §7 (system settings batch, docs/shape-ops-system-config.md
          // §6.4) takes over MConnect credentials.
          {
            path: 'mconnect-credentials',
            element: <MconnectCredentialsPage />,
          },
          // The Registrar entity file (registrars/:idno, UC21 batch) takes over
          // the RSUD resync and the entity's MConnect event history.
          {
            path: 'mconnect-events',
            element: <McnonnectEventsPage />,
          },
          // Gestiune tablouri (dashboards-admin, UC26-02 batch) takes over binding
          // an observer role to the dashboards it may see.
          {
            path: 'observer-roles',
            element: <ObserverRolesPage />,
          },
          // Configurari in the wireframe menu. R3 keeps route segments English.
          {
            path: 'settings',
            element: <SystemConfigPage />,
          },
          {
            path: 'dashboards-admin',
            element: <DashboardsAdminPage />,
          },
          {
            path: 'dashboards-admin/new',
            element: <DashboardFormPage />,
          },
          {
            path: 'dashboards-admin/:slug/edit',
            element: <DashboardFormPage />,
          },
          {
            path: 'sites',
            element: <SitesPage />,
          },
          {
            path: 'sites/new',
            element: <SiteFormPage />,
          },
          {
            // Keyed on the location code, not the row's uuid: the address a person
            // reads names the site the way the API does (ACCS-12, TC-ACCS-12-01).
            path: 'sites/:code/edit',
            element: <SiteFormPage />,
          },
          // Every address nobody declared, under a role segment or not: the
          // reader's own Acasă with the guard's notice, never a not-found page
          // (owner decision Q-1-07, BUG-1-032, BUG-1-061). It sits inside the
          // guard so an anonymous reader is sent to the public home page by the
          // same code that decides every other address, and so a retired one
          // such as `/<role>/inbox` (CF-46) or a section of another role
          // answers exactly like `/<role>/anything`.
          // per docs/shape-ops-route-authorisation.md §Unknown addresses
          {
            path: '*',
            element: <UnknownAddress />,
          },
        ],
      },
    ],
  },
]);
